/**
 * Performance metrics engine — one evaluation engine, many reports.
 *
 * Both the Summary dashboard and the Detail table are computed from these pure
 * functions, so the numbers can never disagree between the two views.
 * See docs/LEXI-PERFORMANCE-REPORTS-SPEC.md §4–§5.
 */
import {
  DEFAULT_LEG_EDITION,
  EDITIONS,
  FIELD_BUCKETS,
  LOW_N_THRESHOLD,
} from "./constants";
import { evaluate, fieldOf, payingPlaces } from "./evaluators";
import { placesPaid } from "./ladders";

const pct = (n, d) => (d > 0 ? (n / d) * 100 : 0);

/** Which edition's #1 tip is the headline "leg" for the current filters. */
function legEdition(filters) {
  return !filters.edition || filters.edition === "all"
    ? DEFAULT_LEG_EDITION
    : filters.edition;
}

function tipsForEdition(race, editionKey) {
  return (race.tips || [])
    .filter((t) => t.edition === editionKey)
    .sort((a, b) => a.rank - b.rank);
}

/** The #1 (rank-1) tip for an edition, or null. */
export function leadTip(race, editionKey) {
  return tipsForEdition(race, editionKey)[0] || null;
}

/**
 * A race counts only if it actually resulted — i.e. at least one runner has a
 * real finishing position. Some upstream dates arrive with every position = 0
 * (results never captured); those aren't scoreable and must be excluded, or
 * they'd falsely read as cashed (0 ≤ any paid cut). Normal dates never have
 * position 0, so this only drops genuinely unresulted races.
 */
export function isResulted(race) {
  return (race?.results || []).some((r) => r.position > 0);
}

/**
 * Filter the race list by track / field-size bucket / date range, and drop any
 * unresulted races. (Edition, report type and lens change *evaluation*, not
 * which races appear.)
 */
export function filterRaces(races, filters = {}) {
  return (races || []).filter((r) => {
    if (!isResulted(r)) return false;
    // "Tipped only" (the default) drops races Lexi didn't tip.
    if (filters.races === "tipped" && !(r.tips && r.tips.length)) return false;
    if (filters.track && filters.track !== "all" && r.track !== filters.track)
      return false;
    if (filters.fieldSize && filters.fieldSize !== "all") {
      const bucket = FIELD_BUCKETS.find((b) => b.key === filters.fieldSize);
      if (bucket && !bucket.test(fieldOf(r))) return false;
    }
    if (filters.from && r.date < filters.from) return false;
    if (filters.to && r.date > filters.to) return false;
    return true;
  });
}

/** Success rate + sample size for the #1 tip of `editionKey` across races. */
function rateForEdition(races, editionKey, type, opts) {
  let cashed = 0;
  let legs = 0;
  for (const r of races) {
    const lead = leadTip(r, editionKey);
    if (!lead) continue;
    legs++;
    if (evaluate(type, r, lead, opts)) cashed++;
  }
  return { rate: pct(cashed, legs), cashed, legs };
}

// Approx place dividend at SP: ~1/4 of the win odds (a common bookie rule).
const placeDividend = (sp) => 1 + (sp - 1) / 4;

/**
 * Approximate ROI at SP for the leg (stretch / "money line"), by bet type:
 *   win      → win odds when 1st
 *   place    → place dividend when placed
 *   each-way → 1/2 stake win + 1/2 stake place (blended) — the staking split is
 *              a sensible default, confirmable with Aaron (spec §11)
 * Flat stake; reacts to the current filters.
 */
function approxRoi(races, editionKey, type, opts) {
  let staked = 0;
  let returned = 0;
  for (const r of races) {
    const lead = leadTip(r, editionKey);
    if (!lead) continue;
    staked += 1;
    const runner = (r.results || []).find(
      (x) => x.selectionId === lead.selectionId,
    );
    const pos = runner?.position ?? null;
    if (pos == null) continue;
    // Price at the selected source; fall back to SP when a Betfair price is
    // missing for this leg (coverage is ~100% but not guaranteed).
    const price =
      (opts.priceSource === "betfair" ? runner?.bf : runner?.sp) ||
      runner?.sp ||
      0;
    const won = pos === 1;
    const placed = pos <= placesPaid(fieldOf(r), opts);
    if (type === "win") {
      if (won) returned += price;
    } else if (type === "eachway") {
      if (won) returned += 0.5 * price;
      if (placed) returned += 0.5 * placeDividend(price);
    } else {
      if (placed) returned += placeDividend(price);
    }
  }
  return staked > 0 ? ((returned - staked) / staked) * 100 : 0;
}

/** Everything the Summary dashboard needs. */
export function computeSummary(races, filters = {}) {
  const type = filters.type || "multi";
  const opts = { lens: filters.lens, priceSource: filters.priceSource };
  const edKey = legEdition(filters);
  const editionScoped = filters.edition && filters.edition !== "all";

  let cashed = 0;
  let legs = 0;
  let anyHits = 0;
  const tracks = new Set();
  const byDate = new Map();

  for (const r of races) {
    tracks.add(r.track);

    const lead = leadTip(r, edKey);
    if (lead) {
      legs++;
      const hit = evaluate(type, r, lead, opts);
      if (hit) cashed++;

      const day = byDate.get(r.date) || { cashed: 0, legs: 0 };
      day.legs++;
      if (hit) day.cashed++;
      byDate.set(r.date, day);
    }

    const scope = editionScoped
      ? tipsForEdition(r, filters.edition)
      : r.tips || [];
    if (scope.some((t) => evaluate(type, r, t, opts))) anyHits++;
  }

  const byEdition = EDITIONS.map((e) => ({
    ...e,
    ...rateForEdition(races, e.key, type, opts),
  }));

  const byFieldSize = FIELD_BUCKETS.map((b) => {
    const inBucket = races.filter((r) => b.test(fieldOf(r)));
    return { ...b, ...rateForEdition(inBucket, edKey, type, opts) };
  });

  const trend = [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, d]) => ({ date, rate: pct(d.cashed, d.legs), n: d.legs }));

  const byTrack = new Map();
  for (const r of races) {
    const lead = leadTip(r, edKey);
    const t = byTrack.get(r.track) || {
      track: r.track,
      races: 0,
      cashed: 0,
      legs: 0,
    };
    t.races++;
    if (lead) {
      t.legs++;
      if (evaluate(type, r, lead, opts)) t.cashed++;
    }
    byTrack.set(r.track, t);
  }
  const leaderboard = [...byTrack.values()]
    .map((t) => ({
      ...t,
      rate: pct(t.cashed, t.legs),
      lowN: t.legs < LOW_N_THRESHOLD,
    }))
    .sort((a, b) => {
      // Real leaders first (enough sample), ranked by rate; low-n sink to bottom.
      if (a.lowN !== b.lowN) return a.lowN ? 1 : -1;
      return b.rate - a.rate;
    });

  return {
    headline: { rate: pct(cashed, legs), cashed, legs },
    anyTip: {
      rate: pct(anyHits, races.length),
      hits: anyHits,
      races: races.length,
    },
    racesAnalysed: races.length,
    tracks: tracks.size,
    roi: approxRoi(races, edKey, type, opts),
    byEdition,
    byFieldSize,
    trend,
    leaderboard,
  };
}

/**
 * Per-race rows for the Detail table. Each row carries the paid-places cut, the
 * "cashed" verdict for the leg, and the full field annotated with tip info.
 */
export function buildDetailRows(races, filters = {}) {
  const type = filters.type || "multi";
  const opts = { lens: filters.lens };
  const edKey = legEdition(filters);

  return races
    .slice()
    .sort(
      (a, b) =>
        (a.date || "").localeCompare(b.date || "") ||
        (a.startTime || "").localeCompare(b.startTime || ""),
    )
    .map((r) => {
      const field = fieldOf(r);
      const paid = payingPlaces(type, r, opts);
      const lead = leadTip(r, edKey);
      const cashed = lead ? evaluate(type, r, lead, opts) === true : false;

      const runners = (r.results || [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((run) => {
          const tip = (r.tips || []).find(
            (t) => t.selectionId === run.selectionId,
          );
          const inRange = run.position <= paid;
          return {
            ...run,
            inRange,
            tip: tip ? { ...tip, hit: inRange } : null,
          };
        });

      return { ...r, field, paid, cashed, lead, runners };
    });
}
